/**
 * ⌘K.
 *
 * DESIGN INTENT
 *   One place to reach anything: a file in any project, a person, a view, or
 *   an action. The index is built from the local replica, so results appear as
 *   the key is released — there is no request, no debounce, and no loading
 *   state, because there is nothing to wait for.
 *
 *   Ranking and highlighting come from @lattice/core's search, which is a pure
 *   function with a test table. That matters here more than anywhere: a
 *   palette that ranks badly is worse than no palette, because the person has
 *   to read the list instead of trusting the first row.
 *
 * DATA DEFINITION
 *   An Item is a SearchDoc whose payload says what to DO when it is chosen.
 *   Interpretation: the palette does not know about files or projects — it
 *   knows about things that can be found and things that can be run. Adding a
 *   new kind of result means adding documents, not editing this component.
 */
import { useEffect, useMemo, useState, useCallback } from 'react'
import { Command } from 'cmdk'
import {
  FolderTree, GitCommitVertical, MessagesSquare, Sun, Moon, Download, Upload,
  CornerDownLeft, Search, Boxes, User as UserIcon, PencilLine, Trash2,
} from 'lucide-react'
import {
  SearchIndex, type SearchDoc, type Match, type ProjectId, type Path,
  basename, describeChange, byNewest,
} from '@lattice/core'
import { parseMarkdown, plainTextOf } from '@/lib/markdown'
import { useWorkspace, filesByProject, personOf } from '@/store/workspace'
import { useUi, type View } from '@/store/ui'
import { useTheme } from '@/hooks/useTheme'
import { FileIcon } from './FileIcon'
import { Avatar, Highlight, Kbd } from './ui/primitives'
import { formatBytes, formatRelative, ellipsisPath } from '@/lib/format'
import { cn } from '@/lib/utils'

/** What choosing a result does. */
type Action =
  | { kind: 'openFile'; projectId: ProjectId; path: string }
  | { kind: 'openProject'; projectId: ProjectId }
  | { kind: 'setView'; view: View }
  | { kind: 'run'; run: () => void }

type Item = SearchDoc<Action>

export function CommandPalette({ onUpload, onDownload, onRename, onDelete }: {
  onUpload: () => void
  onDownload: () => void
  onRename: () => void
  onDelete: () => void
}) {
  const [query, setQuery] = useState('')
  const changes = useWorkspace(s => s.changes)
  const comments = useWorkspace(s => s.comments)
  const projects = useWorkspace(s => s.projects)
  const roster = useWorkspace(s => s.roster)
  const { paletteOpen, setPaletteOpen, go, open, setView, projectId, selection } = useUi()
  const { toggle: toggleTheme, theme } = useTheme()

  /** Rebuild the index only when the replica actually changes. */
  const index = useMemo(() => {
    const idx = new SearchIndex<Action>()
    const docs: Item[] = []

    for (const project of projects) {
      docs.push({
        id: `project:${project.id}`, kind: 'project', title: project.name,
        subtitle: project.description || 'Project', haystack: project.description,
        boost: 1.6, payload: { kind: 'openProject', projectId: project.id as ProjectId },
      })
    }

    // Files from EVERY project: finding a drawing should not require first
    // remembering which project it is in.
    for (const [pid, files] of filesByProject(changes)) {
      const project = projects.find(p => p.id === pid)
      for (const file of files.values()) {
        docs.push({
          id: `file:${pid}:${file.path}`,
          kind: 'file',
          title: basename(file.path),
          subtitle: `${project?.name ?? pid} · ${ellipsisPath(file.path, 3)}`,
          haystack: `${file.path} ${pid}`,
          // The project in view ranks above the others: usually you mean this one.
          boost: pid === projectId ? 1.25 : 1,
          payload: { kind: 'openFile', projectId: pid, path: file.path },
        })
      }
    }

    for (const person of roster) {
      docs.push({
        id: `person:${person.id}`, kind: 'person', title: person.name,
        subtitle: person.email, haystack: person.email, boost: 0.9,
        payload: { kind: 'run', run: () => { setView('activity') } },
      })
    }

    for (const c of [...comments].filter(c => !c.deletedAt).sort(byNewest as never).slice(0, 200)) {
      // Comments are markdown. Showing the source in a result row means a
      // reader searching for "PCD" sees "the **PCD** is `92.2mm`" — the
      // punctuation is noise here, and the same flattening is what we want to
      // search over, so asterisks cannot break a match.
      const plain = plainTextOf(parseMarkdown(c.body))
      docs.push({
        id: `comment:${c.id}`, kind: 'comment',
        title: plain.slice(0, 80),
        subtitle: `${personOf(roster, c.authorId).name} · ${c.path ? basename(c.path as Path) : 'project'}`,
        haystack: plain, boost: 0.8,
        payload: c.path
          ? { kind: 'openFile', projectId: c.projectId, path: c.path }
          : { kind: 'openProject', projectId: c.projectId },
      })
    }

    idx.addAll(docs)
    return idx
  }, [changes, comments, projects, roster, projectId, setView])

  /** Actions are separate: they must be reachable even with an empty query. */
  const actions: Item[] = useMemo(() => ([
    act('upload', 'Upload files…', 'Add files to this project', Upload, onUpload),
    act('download', selection.size ? `Download ${selection.size} selected` : 'Download selection', 'Save files to disk', Download, onDownload),
    act('rename', 'Rename…', 'Rename the selected file or folder', PencilLine, onRename),
    act('delete', 'Delete…', 'Remove the selected files', Trash2, onDelete),
    act('files', 'Go to Files', 'Browse the project tree', FolderTree, () => setView('files')),
    act('activity', 'Go to Activity', 'Who changed what, and when', GitCommitVertical, () => setView('activity')),
    act('comments', 'Go to Comments', 'Recent discussion', MessagesSquare, () => setView('comments')),
    act('theme', theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme', 'Appearance', theme === 'dark' ? Sun : Moon, toggleTheme),
  ]), [selection.size, theme, onUpload, onDownload, onRename, onDelete, setView, toggleTheme])

  const actionIndex = useMemo(() => {
    const idx = new SearchIndex<Action>()
    idx.addAll(actions)
    return idx
  }, [actions])

  const results = useMemo(() => {
    const q = query.trim()
    if (!q) {
      // With no query, show what is most useful cold: actions, then the files
      // that changed most recently — which is almost always what you want.
      const recent = recentFiles(changes, projects, 8)
      return { actions: actions.slice(0, 5).map(toMatch), found: recent }
    }
    return { actions: actionIndex.search(q, { limit: 4 }), found: index.search(q, { limit: 30 }) }
  }, [query, index, actionIndex, actions, changes, projects])

  const perform = useCallback((action: Action) => {
    setPaletteOpen(false)
    setQuery('')
    switch (action.kind) {
      case 'openFile':
        go({ projectId: action.projectId, view: 'files' })
        open(action.path)
        return
      case 'openProject':
        go({ projectId: action.projectId, view: 'files', openPath: undefined })
        return
      case 'setView':
        setView(action.view)
        return
      case 'run':
        action.run()
        return
    }
  }, [go, open, setView, setPaletteOpen])

  // Reset the query each time it opens: a palette that remembers is a palette
  // that shows you yesterday's search.
  useEffect(() => { if (paletteOpen) setQuery('') }, [paletteOpen])

  if (!paletteOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 px-4 pt-[12vh] fade-in"
      onClick={() => setPaletteOpen(false)}
    >
      <Command
        shouldFilter={false}
        loop
        onClick={e => e.stopPropagation()}
        className="pop-in w-full max-w-xl overflow-hidden rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--bg-raised)] shadow-modal"
        onKeyDown={e => { if (e.key === 'Escape') setPaletteOpen(false) }}
      >
        <div className="flex items-center gap-2 border-b border-[var(--border)] px-3">
          <Search className="h-4 w-4 shrink-0 text-[var(--fg-subtle)]" strokeWidth={1.75} />
          <Command.Input
            autoFocus
            value={query}
            onValueChange={setQuery}
            placeholder="Search files, people, actions…"
            className="h-11 flex-1 bg-transparent text-base outline-none placeholder:text-[var(--fg-subtle)]"
          />
          <Kbd keys={['Esc']} />
        </div>

        <Command.List className="max-h-[52vh] overflow-y-auto p-1.5">
          <Command.Empty className="px-3 py-8 text-center text-sm text-[var(--fg-subtle)]">
            Nothing matches “{query}”.
          </Command.Empty>

          {results.actions.length > 0 && (
            <Group heading="Actions">
              {results.actions.map(m => (
                <Row key={m.doc.id} match={m} onSelect={() => perform(m.doc.payload)} />
              ))}
            </Group>
          )}

          {results.found.length > 0 && (
            <Group heading={query ? 'Results' : 'Recently changed'}>
              {results.found.map(m => (
                <Row key={m.doc.id} match={m} onSelect={() => perform(m.doc.payload)} />
              ))}
            </Group>
          )}
        </Command.List>

        <div className="flex items-center gap-3 border-t border-[var(--border)] px-3 py-1.5 text-[11px] text-[var(--fg-subtle)]">
          <span className="flex items-center gap-1"><Kbd keys={['↑', '↓']} /> navigate</span>
          <span className="flex items-center gap-1"><Kbd keys={['↵']} /> open</span>
          <div className="flex-1" />
          <span>{results.found.length} of {index.size.toLocaleString()} indexed</span>
        </div>
      </Command>
    </div>
  )
}

function Group({ heading, children }: { heading: string; children: React.ReactNode }) {
  return (
    <Command.Group
      heading={heading}
      className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-1.5 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-[var(--fg-subtle)]"
    >
      {children}
    </Command.Group>
  )
}

function Row({ match, onSelect }: { match: Match<Action>; onSelect: () => void }) {
  const { doc, spans } = match
  return (
    <Command.Item
      value={doc.id}
      onSelect={onSelect}
      className={cn(
        'snap flex cursor-pointer items-center gap-2.5 rounded-[var(--radius-md)] px-2 py-1.5 text-sm',
        'data-[selected=true]:bg-[var(--bg-hover)]',
      )}
    >
      <ResultIcon doc={doc} />
      <span className="min-w-0 flex-1">
        <span className="block truncate"><Highlight text={doc.title} spans={spans} /></span>
        {doc.subtitle && <span className="block truncate text-xs text-[var(--fg-subtle)]">{doc.subtitle}</span>}
      </span>
      <CornerDownLeft className="h-3 w-3 shrink-0 text-[var(--fg-subtle)] opacity-0 data-[selected=true]:opacity-100" strokeWidth={1.75} />
    </Command.Item>
  )
}

function ResultIcon({ doc }: { doc: Item }) {
  if (doc.kind === 'file') return <FileIcon path={doc.title} />
  if (doc.kind === 'project') return <Boxes className="h-3.5 w-3.5 shrink-0 text-[var(--fg-muted)]" strokeWidth={1.75} />
  if (doc.kind === 'person') return <UserIcon className="h-3.5 w-3.5 shrink-0 text-[var(--fg-muted)]" strokeWidth={1.75} />
  if (doc.kind === 'comment') return <MessagesSquare className="h-3.5 w-3.5 shrink-0 text-[var(--fg-muted)]" strokeWidth={1.75} />
  const Icon = (doc as unknown as { icon?: typeof Search }).icon ?? Search
  return <Icon className="h-3.5 w-3.5 shrink-0 text-[var(--fg-muted)]" strokeWidth={1.75} />
}

/** Build an action document. */
function act(
  id: string, title: string, subtitle: string,
  icon: typeof Search, run: () => void,
): Item {
  return {
    id: `action:${id}`, kind: 'command', title, subtitle, haystack: `${title} ${subtitle}`,
    boost: 1.4, payload: { kind: 'run', run },
    // Carried for the icon lookup; harmless extra field on the document.
    ...({ icon } as object),
  } as Item
}

const toMatch = (doc: Item): Match<Action> => ({ doc, score: doc.boost, spans: [] })

/** The files changed most recently, across every project. */
function recentFiles(
  changes: readonly import('@lattice/core').Change[],
  projects: readonly { id: string; name: string }[],
  limit: number,
): Match<Action>[] {
  const seen = new Set<string>()
  const out: Match<Action>[] = []
  for (const c of [...changes].sort(byNewest)) {
    if (c.kind === 'deleted') continue
    const key = `${c.projectId}|${c.path}`
    if (seen.has(key)) continue
    seen.add(key)
    const project = projects.find(p => p.id === c.projectId)
    out.push({
      doc: {
        id: `recent:${key}`, kind: 'file', title: basename(c.path),
        subtitle: `${project?.name ?? c.projectId} · ${formatRelative(c.at)} · ${c.size ? formatBytes(c.size) : ''}`,
        haystack: c.path, boost: 1,
        payload: { kind: 'openFile', projectId: c.projectId, path: c.path },
      },
      score: 1, spans: [],
    })
    if (out.length >= limit) break
  }
  return out
}

export { describeChange }
